<script lang="ts">
  import Button from '@/lib/components/ui/button/button.svelte';
  import { fileIconClass, validateAttachment, type AttachmentKind } from './attachments';
  import AttachMenuPopover from './AttachMenuPopover.svelte';
  import type { ExerciseReference } from './api';

  export let sending: boolean;
  export let onSend: (text: string, files: File[], references: ExerciseReference[], replyToId?: string) => void;
  export let onError: (message: string) => void;
  // Kept as a small resolved shape ({id, label, preview}), not the full ChatMessage/Comment -- the
  // composer has no participant data to resolve a sender's display name itself, so the parent
  // (which does) resolves it before handing this down.
  export let replyTo: { id: string; label: string; preview: string } | null = null;
  export let onCancelReply: () => void = () => {};
  export let editing: { id: string; body: string } | null = null;
  export let onCancelEdit: () => void = () => {};
  export let onEdit: (id: string, body: string) => void = () => {};
  // Shared by ConversationPane.svelte (Messaging) and CommentThreadPanel.svelte (Comments) --
  // the only real difference between the two former composers was this handful of user-facing
  // strings, so this prop is the whole seam between the two features.
  export let noun: 'message' | 'comment' = 'message';

  $: labels =
    noun === 'comment'
      ? { editingBanner: 'Editing comment', placeholder: 'Write a comment...', editPlaceholder: 'Edit your comment...', submitTitle: 'Post' }
      : { editingBanner: 'Editing message', placeholder: 'Write a message...', editPlaceholder: 'Edit your message...', submitTitle: 'Send' };

  const MAX_ATTACHMENTS = 10;

  type StagedFile = { entryType: 'file'; file: File; fileKind: AttachmentKind; previewUrl?: string };
  type StagedExercise = { entryType: 'exercise'; exerciseType: 'activity' | 'workout'; path: string; name: string };
  type StagedItem = StagedFile | StagedExercise;

  let bodyText = '';
  let staged: StagedItem[] = [];
  let fileInput: HTMLInputElement;

  // Edit mode is body-text-only (no attachment editing) -- pre-fill the textarea with the
  // message's current body exactly once when editing starts, not on every keystroke.
  let editingId: string | null = null;
  $: if (editing && editing.id !== editingId) {
    bodyText = editing.body;
    editingId = editing.id;
  } else if (!editing && editingId != null) {
    bodyText = '';
    editingId = null;
  }

  function revokeStagedPreviews() {
    staged.forEach((s) => {
      if (s.entryType === 'file' && s.previewUrl) URL.revokeObjectURL(s.previewUrl);
    });
  }

  function cancelEdit() {
    bodyText = '';
    editingId = null;
    onCancelEdit();
  }

  function submit() {
    if (sending) return;
    const text = bodyText.trim();

    if (editing) {
      if (!text) return;
      const id = editing.id;
      bodyText = '';
      editingId = null;
      onEdit(id, text);
      onCancelEdit();
      return;
    }

    if (!text && staged.length === 0) return;
    const files = staged.filter((s): s is StagedFile => s.entryType === 'file').map((s) => s.file);
    const references = staged
      .filter((s): s is StagedExercise => s.entryType === 'exercise')
      .map((s) => ({ type: s.exerciseType, path: s.path }));
    const replyToId = replyTo?.id;
    revokeStagedPreviews();
    bodyText = '';
    staged = [];
    onSend(text, files, references, replyToId);
    if (replyTo) onCancelReply();
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';

    for (const file of files) {
      if (staged.length >= MAX_ATTACHMENTS) {
        onError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        break;
      }
      const result = validateAttachment(file);
      if ('error' in result) {
        onError(result.error);
        continue;
      }
      const previewUrl = result.kind === 'image' ? URL.createObjectURL(file) : undefined;
      staged = [...staged, { entryType: 'file', file, fileKind: result.kind, previewUrl }];
    }
  }

  function onPickExercise(type: 'activity' | 'workout', path: string, name: string) {
    if (staged.length >= MAX_ATTACHMENTS) {
      onError(`You can attach up to ${MAX_ATTACHMENTS} items.`);
      return;
    }
    staged = [...staged, { entryType: 'exercise', exerciseType: type, path, name }];
  }

  function removeStaged(index: number) {
    const removed = staged[index];
    if (removed?.entryType === 'file' && removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    staged = staged.filter((_, i) => i !== index);
  }
</script>

<div class="flex shrink-0 flex-col gap-2 border-t border-border px-3 py-2.5">
  {#if editing}
    <div class="flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
      <i class="fa-solid fa-pen"></i>
      <span class="flex-1">{labels.editingBanner}</span>
      <button type="button" on:click={cancelEdit} title="Cancel edit"><i class="fa-solid fa-xmark"></i></button>
    </div>
  {:else if replyTo}
    <div class="flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
      <i class="fa-solid fa-reply"></i>
      <span class="min-w-0 flex-1 truncate">Replying to {replyTo.label}: {replyTo.preview}</span>
      <button type="button" on:click={onCancelReply} title="Cancel reply"><i class="fa-solid fa-xmark"></i></button>
    </div>
  {/if}
  {#if staged.length > 0 && !editing}
    <div class="flex flex-wrap gap-2">
      {#each staged as item, i}
        {#if item.entryType === 'file' && item.fileKind === 'file'}
          <div class="flex h-14 max-w-[140px] items-center gap-1.5 rounded-md border border-border bg-muted px-2 text-xs">
            <i class="fa-solid {fileIconClass(item.file.name)} shrink-0 text-muted-foreground"></i>
            <span class="truncate">{item.file.name}</span>
            <button type="button" class="ml-auto shrink-0 text-muted-foreground hover:text-foreground" on:click={() => removeStaged(i)} title="Remove">
              <i class="fa-solid fa-xmark text-[10px]"></i>
            </button>
          </div>
        {:else if item.entryType === 'file'}
          <div class="relative">
            {#if item.fileKind === 'image'}
              <img src={item.previewUrl} alt="attachment preview" class="h-14 w-14 rounded-md object-cover" />
            {:else}
              <div class="flex h-14 w-14 items-center justify-center rounded-md border border-border bg-muted">
                <i class="fa-solid {item.fileKind === 'video' ? 'fa-file-video' : 'fa-file-audio'} text-muted-foreground"></i>
              </div>
            {/if}
            <button
              type="button"
              class="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-background"
              on:click={() => removeStaged(i)}
              title="Remove"
            >
              <i class="fa-solid fa-xmark text-[9px]"></i>
            </button>
          </div>
        {:else}
          <div class="flex h-14 max-w-[140px] items-center gap-1.5 rounded-md border border-border bg-muted px-2 text-xs">
            <i class="fa-solid {item.exerciseType === 'workout' ? 'fa-dumbbell' : 'fa-person-running'} shrink-0 text-muted-foreground"></i>
            <span class="truncate">{item.name}</span>
            <button type="button" class="ml-auto shrink-0 text-muted-foreground hover:text-foreground" on:click={() => removeStaged(i)} title="Remove">
              <i class="fa-solid fa-xmark text-[10px]"></i>
            </button>
          </div>
        {/if}
      {/each}
    </div>
  {/if}
  <div class="flex items-center gap-2">
    <!-- No accept filter -- images/video/audio still get their own preview, everything else
    (.fit, PDFs, ...) is a generic downloadable file. validateAttachment() is what actually
    enforces the blocked-extension/size rules, same as the server. -->
    <input type="file" multiple bind:this={fileInput} on:change={onFileSelected} class="hidden" />
    {#if !editing}
      <AttachMenuPopover onFile={() => fileInput.click()} {onPickExercise} disabled={sending} />
    {/if}
    <!-- Grows with its content up to max-h-28 (then scrolls, via overflow-y-auto below),
     -- see the autogrow-wrap rules at the bottom of this file. -->
    <div
      class="autogrow-wrap min-h-[36px] max-h-28 w-full flex-1 overflow-y-auto rounded-lg border border-input bg-transparent px-3.5 py-2 text-sm leading-relaxed shadow-sm focus-within:outline-none focus-within:ring-1 focus-within:ring-primary"
      data-replicated-value={bodyText}
    >
      <textarea rows="1" class="placeholder:text-muted-foreground" placeholder={editing ? labels.editPlaceholder : labels.placeholder} bind:value={bodyText} on:keydown={onKeydown}></textarea>
    </div>
    <Button size="icon" class="shrink-0" on:click={submit} disabled={sending || (!bodyText.trim() && staged.length === 0)} title={editing ? 'Save' : labels.submitTitle}>
      {#if sending}
        <i class="fa-solid fa-spinner-third fa-spin"></i>
      {:else if editing}
        <i class="fa-solid fa-check text-sm"></i>
      {:else}
        <i class="fa-solid fa-paper-plane text-sm"></i>
      {/if}
    </Button>
  </div>
</div>

<style>
  /* Classic CSS-only auto-growing textarea: the real textarea and an invisible ::after (mirroring
     the text via data-replicated-value) share one grid cell. The mirror's content sets the row's
     height, the textarea just stretches to match -- no JS measuring or style writes involved.
     Padding/font/color live on .autogrow-wrap itself (via the Tailwind classes above) rather than
     on the children, so they're applied exactly once instead of stacking. */
  .autogrow-wrap {
    display: grid;
  }
  .autogrow-wrap::after {
    content: attr(data-replicated-value) ' ';
    white-space: pre-wrap;
    visibility: hidden;
  }
  .autogrow-wrap > textarea,
  .autogrow-wrap::after {
    grid-area: 1 / 1 / 2 / 2;
    padding: 0;
    border: 0;
    font: inherit;
    color: inherit;
    word-break: break-word;
  }
  .autogrow-wrap > textarea {
    resize: none;
    overflow: hidden;
    background: transparent;
    outline: none;
  }
</style>
